/**
 * "Would *this browser* render the site right now?"
 *
 * The server-side probe (api/src/sessions/site-probe.ts) answers a different
 * question than the one the frame is about to ask. It fetches from the API, on
 * the API's network path, with the API's cache. The frame fetches from here.
 * The two disagree exactly when it matters:
 *
 *  - during the routing gap, the deployed bucket answers the API while the
 *    platform's pre-deploy backend is still answering this browser — and that
 *    backend returns a real `404` for `/assets/*`, with no
 *    `Access-Control-Allow-Origin`, which is the CORS-plus-404 pair seen in the
 *    console;
 *  - after it, because a browser that already collected one of those failures
 *    keeps serving it back to itself. The site's filenames are content-hashed,
 *    so a rebuild of the same site produces the same asset URLs, and a poisoned
 *    entry outlives the deploy that caused it;
 *  - and because Hatch publishes a warm-up placeholder to this same address
 *    during provisioning, a cache entry collected in that window is not a
 *    broken half-response that fails on its own. It is a complete,
 *    self-consistent site — shell and matching bundles — that simply is not the
 *    user's. Nothing but the build identity separates the two.
 *
 * Neither is visible from the parent page once the frame is mounted: the site
 * is cross-origin, so a frame whose bundles 404'd looks exactly like one that
 * rendered. Hence a rehearsal, run before the frame is given the URL.
 *
 * `cache: "reload"` is what makes this a fix rather than a warning. It forces
 * the request past the browser's copy and writes the fresh response back, so a
 * poisoned entry is repaired by the same pass that detects it.
 *
 * The deploy target serves `Access-Control-Allow-Origin: *`, which is what
 * makes any of this readable from here. A host that does not is not judged
 * harshly for it — see the budget in `useSiteReady`.
 */

import { buildIdMarker } from "@hatch/shared";

/** Long enough for a cold edge; short enough to retry on top of. */
export const SITE_VERIFY_TIMEOUT_MS = 5000;

/**
 * How many of the shell's assets are checked.
 *
 * A Vite/React build emits one module script and one stylesheet. A prerendered
 * SvelteKit build emits one stylesheet and a modulepreload link per chunk —
 * eight on a two-route site, more as routes are added — so the old bound of 4
 * would rehearse a fraction of them and call the rest verified. The cap bounds
 * a pathological document, and mirrors the server probe.
 */
const MAX_ASSETS_CHECKED = 12;

/**
 * Asset URLs the shell needs before it can render anything.
 *
 * Parsed rather than regexed because the browser has a real parser, and
 * `DOMParser` builds the tree without fetching anything in it.
 *
 * `modulepreload` is included for the reason spelled out in the server probe:
 * a prerendered SvelteKit shell has no `<script src>` whatsoever, so reading
 * only `src` and `stylesheet` left the frame's JS entirely unrehearsed. The
 * `~=` match is what keeps `rel="preload"` out — a preloaded font is not
 * something the page fails without.
 */
export function assetUrlsFrom(html: string, documentUrl: string): string[] {
  const origin = new URL(documentUrl).origin;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const refs = [
    ...[...doc.querySelectorAll("script[src]")].map((el) => el.getAttribute("src")),
    // One selector rather than two passes, so the links stay in document order
    // — the entry chunks a SvelteKit shell lists first are the ones worth
    // checking first if the cap ever truncates.
    ...[
      ...doc.querySelectorAll(
        'link[rel~="stylesheet"][href], link[rel~="modulepreload"][href]',
      ),
    ].map((el) => el.getAttribute("href")),
  ];

  const found = new Set<string>();
  for (const ref of refs) {
    if (!ref) continue;
    let resolved: URL;
    try {
      resolved = new URL(ref, documentUrl);
    } catch {
      continue;
    }
    // Cross-origin assets are the site's business, not ours: a CDN font that
    // refuses us is not a broken deploy.
    if (resolved.origin !== origin) continue;
    found.add(resolved.href);
    if (found.size >= MAX_ASSETS_CHECKED) break;
  }
  return [...found];
}

/** The SPA fallback wearing an asset's URL — a 200 the browser cannot execute. */
function isFallbackHtml(contentType: string | null): boolean {
  return (contentType ?? "").toLowerCase().includes("text/html");
}

/** One request, past the cache, with the timeout handled. Never throws. */
async function refetch(url: string, signal?: AbortSignal): Promise<Response | null> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort);
  const timer = setTimeout(abort, SITE_VERIFY_TIMEOUT_MS);
  try {
    return await fetch(url, {
      // The frame's own requests are CORS-mode — Vite marks both tags
      // `crossorigin` — so anything less would rehearse the wrong request.
      mode: "cors",
      credentials: "omit",
      cache: "reload",
      redirect: "follow",
      signal: controller.signal,
    });
  } catch {
    // A CORS rejection lands here too, which is the point: the pre-deploy 404
    // has no ACAO, so the failure the frame would hit is the failure we see.
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

/**
 * `true` only when *this build's* shell and its bundles just served to this
 * browser.
 *
 * A `false` is "not yet", never "broken": every failure mode here — routing
 * gap, cold TLS, a host that withholds CORS — is either transient or not the
 * site's fault, and the caller decides how long to keep asking.
 *
 * `expectedBuildId` is what makes this more than a liveness check. Hatch
 * publishes a warm-up placeholder to the same address during provisioning, so
 * a cache entry poisoned during that window is not a broken half-response that
 * fails on its own — it is a complete, self-consistent site, shell and matching
 * bundles, that simply is not the user's. Only the identity separates them.
 * Omit it and this stays the older liveness check, which is correct for a
 * deploy that could not be stamped and for the reload button's repair pass.
 */
export async function verifySiteFromBrowser(
  siteUrl: string,
  signal?: AbortSignal,
  expectedBuildId?: string,
): Promise<boolean> {
  const shell = await refetch(siteUrl, signal);
  if (!shell?.ok) return false;

  let html: string;
  try {
    html = await shell.text();
  } catch {
    return false;
  }

  // Checked after the re-fetch, never before: the point is to judge the bytes
  // this pass just wrote over the cache, so a mismatch is both the detection
  // and the repair.
  if (expectedBuildId && !html.includes(buildIdMarker(expectedBuildId))) return false;

  const assets = assetUrlsFrom(html, siteUrl);
  // A shell that references nothing is judged on having served at all. The
  // server probe has already checked this document is the deployed one.
  if (assets.length === 0) return true;

  const results = await Promise.all(assets.map((a) => refetch(a, signal)));
  return results.every((res) => res?.ok === true && !isFallbackHtml(res.headers.get("content-type")));
}
